// components/finance/sections/FinancialHealth.jsx

import React, { useState, useMemo } from 'react';
import { 
  ChartBarIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  UsersIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import MetricCard from '../../common/MetricCard';
import MetricDetailsModal from '../../common/MetricDetailsModal';
import { formatCurrency } from '../../../utils';
import { useFinancialHealth } from '../../../hooks/finance/useFinancialHealth';
import { useMaxDates } from '../../../hooks/finance/useMaxDates';

// KPI Definitions
const KPI_DEFINITIONS = {
  profitMargin: "Net profit as a percentage of total revenue. Indicates how much profit the company makes for every dollar of revenue generated.",
  netIncome: "Total revenue minus all expenses, including operating expenses, COGS, and other expenses. The bottom line profit of the company.", // ✅ NEW
  grossProfit: "Adjusted Gross Income - Total revenue minus cost of goods sold and certain adjustments. Represents the company's gross profitability before operating expenses.",
  avgRetainerRevenue: "Average monthly recurring revenue from retainer-based client contracts. Provides stability and predictability to cash flow.",
  debtOutstanding: "Total amount of outstanding debt obligations. Lower values indicate better financial health and less financial risk.",
  headCount: "Total number of active employees. Key indicator of company size and growth trajectory.",
  laborMargin: "Revenue minus labor costs as a percentage of revenue. Measures efficiency of labor utilization and profitability.",
  revPerFTE: "Revenue per Full-Time Equivalent employee. Measures employee productivity and operational efficiency.",
  employeeSatisfaction: "Average employee satisfaction rating on a scale of 1-5. Higher scores indicate better workplace morale and retention."
};

// Helper function to format date range based on selected year and actual transaction data
const formatDateRange = (dateRange, maxDateUpdated) => {
  if (!dateRange || !dateRange.start_date) {
    return null;
  }
  
  // Extract year from start_date string to avoid timezone issues
  const selectedYear = parseInt(dateRange.start_date.split('-')[0], 10);
  const ytdStart = new Date(selectedYear, 0, 1); // January 1st of the selected year
  
  // Determine the end date based on selected year and max transaction date
  let ytdEnd;
  const currentYear = new Date().getFullYear();
  
  if (selectedYear === currentYear) {
    // For current year: use the max transaction date from API (most recent data available)
    if (maxDateUpdated) {
      const [year, month, day] = maxDateUpdated.split('-').map(num => parseInt(num, 10));
      ytdEnd = new Date(year, month - 1, day); // month - 1 because JS months are 0-indexed
    } else {
      // Fallback to today if API data not available
      ytdEnd = new Date();
    }
  } else {
    // For past years: show full year (Jan 1 - Dec 31 of selected year)
    ytdEnd = new Date(selectedYear, 11, 31); // December 31st of selected year
  }
  
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  return `${formatDate(ytdStart)} - ${formatDate(ytdEnd)}`;
};

export const FinancialHealth = ({ dateRange }) => {
  // Use the financial health hook - now returns YTD averages and comparison with last year
  const {
    metrics,
    previousMetrics,
    monthlyData,
    loading,
    error,
  } = useFinancialHealth(dateRange);

  // Fetch max dates from API to get actual transaction date range
  const { maxDates } = useMaxDates();
  
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null,
    title: '',
    data: [],
    dataType: 'percentage',
    showTotal: true,  // ✅ Default to true
  });

  // Determine if current selection is YTD or Full Year
  const isYTD = useMemo(() => {
    if (!dateRange?.start_date || !dateRange?.end_date) {
      return true; // Default to YTD
    }
    
    const selectedYear = parseInt(dateRange.start_date.split('-')[0], 10);
    const endDate = dateRange.end_date;
    const endOfYear = `${selectedYear}-12-31`;
    
    // If end date is before December 31st of the selected year, it's YTD
    return endDate < endOfYear;
  }, [dateRange]);

  // Create period label with year
  const periodLabel = useMemo(() => {
    if (!dateRange?.start_date) return 'YTD';
    
    const selectedYear = parseInt(dateRange.start_date.split('-')[0], 10);
    const currentYear = new Date().getFullYear();
    
    // Current year is always YTD (unless we're on Dec 31)
    if (selectedYear === currentYear) {
      return `${selectedYear} YTD`;
    }
    
    // Past years that end on Dec 31 are "Full Year"
    if (!isYTD) {
      return `${selectedYear}`;
    }
    
    return `${selectedYear} YTD`;
  }, [dateRange, isYTD]);

  // Format the date range for display using actual max transaction date from API
  const formattedDateRange = formatDateRange(dateRange, maxDates.maxDateUpdated);
  
  // Helper function to calculate YoY change percentage
  const calculateYoYChange = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  // Get last year label from dateRange
  const lastYearLabel = dateRange?.start_date 
    ? (parseInt(dateRange.start_date.split('-')[0], 10) - 1).toString()
    : null;

  const openModal = (type) => {
    const modalConfig = {
      profitMargin: {
        title: 'Profit Margin',
        data: monthlyData.profitMargin,
        dataType: 'percentage',
        showTotal: false,  // ✅ Don't show total for percentages
      },
      netIncome: {
        title: 'Net Income',
        data: monthlyData.netIncome,
        dataType: 'currency',
        showTotal: true,  // ✅ Show total for currency
      },
      grossProfit: {
        title: 'Gross Profit',
        data: monthlyData.grossProfit,
        dataType: 'currency',
        showTotal: true,  // ✅ Show total for currency
      },
      avgRetainerRevenue: {
        title: 'Total Retainer Revenue',
        data: monthlyData.avgRetainerRevenue,
        dataType: 'currency',
        showTotal: true,  // ✅ Show total for currency
      },
      headCount: {
        title: 'Active Employees',
        data: monthlyData.headCount,
        dataType: 'number',
        showTotal: false,  // ✅ Don't show total for counts
      },
    };

    const config = modalConfig[type];
    if (config) {
      setModalState({
        isOpen: true,
        type,
        ...config,
      });
    }
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      type: null,
      title: '',
      data: [],
      dataType: 'percentage',
      showTotal: true,
    });
  };

  const RotatedArrowUpIcon = (props) => <ArrowUpIcon {...props} className={`${props.className || ''} rotate-45`} />;
  
  // Determine view mode based on modal state
  const getViewMode = (metricType) => {
    return modalState.isOpen && modalState.type === metricType ? "MONTHLY" : "YTD";
  };

  return (
    <>
      <MetricDetailsModal
        title={modalState.title}
        monthlyData={modalState.data}
        isOpen={modalState.isOpen}
        onClose={closeModal}
        dataType={modalState.dataType}
        showTotal={modalState.showTotal}  // ✅ Pass the flag
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center mb-4">
          <ExclamationCircleIcon className="h-5 w-5 text-red-600 mr-2" />
          <span className="text-red-700">Error loading financial health: {error}</span>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        <MetricCard
          title={`Profit Margin`}
          tooltip={KPI_DEFINITIONS.profitMargin}
          value={`${metrics.profitMargin.toFixed(1)}%`}
          change={calculateYoYChange(metrics.profitMargin, previousMetrics.profitMargin)}  // ✅ CHANGED
          changeLabel="vs"
          lastYearLabel={lastYearLabel}
          lastYearValue={`${previousMetrics.profitMargin.toFixed(1)}%`}
          icon={ChartBarIcon}
          loading={loading}
          actionIcon={modalState.isOpen && modalState.type === 'profitMargin' ? ArrowDownIcon : RotatedArrowUpIcon}
          onClick={() => openModal('profitMargin')}
          isClickable={true}
          viewMode={getViewMode('profitMargin')}
          dateRange={formattedDateRange}
          periodLabel={`${periodLabel}`}
        />

        <MetricCard
          title="Net Income"
          tooltip={KPI_DEFINITIONS.netIncome}
          value={formatCurrency(metrics.netIncome)}
          change={calculateYoYChange(metrics.netIncome, previousMetrics.netIncome)}  // ✅ CHANGED
          changeLabel="vs"
          lastYearLabel={lastYearLabel}
          lastYearValue={formatCurrency(previousMetrics.netIncome)}  // ✅ CHANGED - use previousMetrics
          icon={CurrencyDollarIcon}
          loading={loading}
          actionIcon={modalState.isOpen && modalState.type === 'netIncome' ? ArrowDownIcon : RotatedArrowUpIcon}
          onClick={() => openModal('netIncome')}
          isClickable={true}
          viewMode={getViewMode('netIncome')}
          dateRange={formattedDateRange}
          periodLabel={`${periodLabel}`}
        />

        <MetricCard
          title="Adjusted Gross Income"
          tooltip={KPI_DEFINITIONS.grossProfit}
          value={formatCurrency(metrics.grossProfit)}
          change={calculateYoYChange(metrics.grossProfit, previousMetrics.grossProfit)}
          changeLabel="vs"
          lastYearLabel={lastYearLabel}
          lastYearValue={formatCurrency(previousMetrics.grossProfit)}
          icon={CurrencyDollarIcon}
          loading={loading}
          actionIcon={modalState.isOpen && modalState.type === 'grossProfit' ? ArrowDownIcon : RotatedArrowUpIcon}
          onClick={() => openModal('grossProfit')}
          isClickable={true}
          viewMode={getViewMode('grossProfit')}
          dateRange={formattedDateRange}
          periodLabel={`${periodLabel}`}
        />

        <MetricCard
          title="Total Retainer Revenue"
          tooltip={KPI_DEFINITIONS.avgRetainerRevenue}
          value={formatCurrency(metrics.avgRetainerRevenue)}
          change={calculateYoYChange(metrics.avgRetainerRevenue, previousMetrics.avgRetainerRevenue)}  // ✅ CHANGED
          changeLabel="vs"
          lastYearLabel={lastYearLabel}
          lastYearValue={formatCurrency(previousMetrics.avgRetainerRevenue)}  // ✅ CHANGED - use previousMetrics
          icon={ArrowPathIcon}
          loading={loading}
          actionIcon={modalState.isOpen && modalState.type === 'avgRetainerRevenue' ? ArrowDownIcon : RotatedArrowUpIcon}
          onClick={() => openModal('avgRetainerRevenue')}
          isClickable={true}
          viewMode={getViewMode('avgRetainerRevenue')}
          dateRange={formattedDateRange}
          periodLabel={`${periodLabel}`}
        />

        <MetricCard
          title="Debt Outstanding"
          tooltip={KPI_DEFINITIONS.debtOutstanding}
          value={formatCurrency(metrics.debtOutstanding)}
          change={null}
          changeLabel={`As of ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
          icon={ExclamationCircleIcon}
          loading={loading}
          isClickable={false}
          inverseChangeColor={true}
          viewMode="YTD"
          dateRange={formattedDateRange}
          periodLabel={`${periodLabel}`}
        />

        <MetricCard
          title="Head Count"
          tooltip={KPI_DEFINITIONS.headCount}
          value={metrics.headCount > 0 ? Math.round(metrics.headCount).toString() : 'TBD'}
          change={calculateYoYChange(metrics.headCount, previousMetrics.headCount)}  // ✅ CHANGED
          changeLabel="vs"
          lastYearLabel={lastYearLabel}
          lastYearValue={previousMetrics.headCount > 0 ? Math.round(previousMetrics.headCount).toString() : 'N/A'}  // ✅ CHANGED - use previousMetrics
          icon={UsersIcon}
          loading={loading}
          actionIcon={modalState.isOpen && modalState.type === 'headCount' ? ArrowDownIcon : RotatedArrowUpIcon}
          onClick={() => openModal('headCount')}
          isClickable={metrics.headCount > 0}
          viewMode={getViewMode('headCount')}
          dateRange={formattedDateRange}
          periodLabel={`${periodLabel}`}
        />
      </div>
    </>
  );
};

export default FinancialHealth;